/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty, isEqual } from 'lodash';

import ActionButton from 'components/buttons/action-button/ActionButton';
import ListsInfluencerCards from './ListsInfluencerCards';

import { fetchInfluencersRecentPosts } from 'modules/influencers/reducers/influencerRecentPosts';
import { fetchInfluencersMeta } from 'modules/influencers/reducers/influencersMeta';
import { fetchInfluencersHistory } from 'modules/lists/reducers/lists';
import CreateNewProfilesButton from 'modules/create-new-profiles/components/create-new-profiles-button/CreateNewProfilesButton';
import ListImportTaskStatusBanners from './import-task-status-banners/ListImportTaskStatusBanners';

import { t } from 'utils/language';
import { SMART_LIST_TYPE } from 'utils/list';

import { getCreatorIdsGroupedByMetricChannels } from 'modules/discovery/creators/utils/creators';
import { cancelAllCancelablePromises } from 'modules/influencers/utils/promise-handler';

import type { DiscoveryCreatorsFilters, InfluencerTile, List, ListContextMenu } from 'types';
import type { RecentPostsDataType } from 'modules/influencers/types/RecentPosts.type';

type OwnProps = {
  orgId: number,
  influencers: Array<InfluencerTile>,
  canManageList: boolean,
  contextMenuProps: ListContextMenu,
  expandedIdsMap: {
    [type: number]: string
  },
  updateCreatorTileSize?: (size: string, creatorId: number) => void,
  onDelete: (a: InfluencerTile) => void
};

type StateProps = {
  influencerRecentPosts: {
    [x: number]: RecentPostsDataType
  },
  currentList: List
};

type DispatchProps = {
  dispatchFetchInfluencersRecentPosts: (
    e: Array<number>,
    d: string,
    c: DiscoveryCreatorsFilters,
    b: number,
    a: boolean
  ) => void,
  dispatchFetchInfluencersMeta: (a: Array<number>) => void,
  dispatchFetchInfluencersHistory: (b: number, a: Array<number>) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  groupedCreatorIds: {
    [x: string]: Array<number>
  },
  noImportTaskBannersShown: boolean
};

class ListsResults extends React.PureComponent<Props, State> {
  state: State = {
    groupedCreatorIds: {},
    noImportTaskBannersShown: false
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      orgId,
      influencers,
      dispatchFetchInfluencersRecentPosts,
      dispatchFetchInfluencersMeta,
      dispatchFetchInfluencersHistory
    } = this.props;

    const creatorsList = influencers.filter(creator => !creator.loading).map(creator => creator.id);
    const prevCreatorList = prevProps.influencers.filter(creator => !creator.loading).map(creator => creator.id);
    const hasNewCreatorsList = !isEmpty(creatorsList) && !isEqual(creatorsList, prevCreatorList);

    if (hasNewCreatorsList) {
      dispatchFetchInfluencersMeta(creatorsList);
      dispatchFetchInfluencersHistory(orgId, creatorsList);

      // group creators by first visible channel and fetch them separately
      const groupedCreatorIds = getCreatorIdsGroupedByMetricChannels(influencers);

      cancelAllCancelablePromises();
      Object.keys(groupedCreatorIds).forEach((channel) => {
        dispatchFetchInfluencersRecentPosts(
          groupedCreatorIds[channel],
          channel,
          {} as DiscoveryCreatorsFilters,
          1,
          false
        );
      });

      if (groupedCreatorIds !== prevState.groupedCreatorIds) {
        this.setState({
          groupedCreatorIds
        });
      }
    }
  }

  componentWillUnmount() {
    cancelAllCancelablePromises();
  }

  handleFetchCreatorRecentPosts = (id: number, channel: string, refetch: boolean) => {
    const { influencerRecentPosts, dispatchFetchInfluencersRecentPosts } = this.props;
    const page = (refetch)
      ? 1
      : (influencerRecentPosts[id]?.lastFetchedPage || 1) + 1;

    if (page === 1 || (page > 1 && influencerRecentPosts[id]?.hasMorePages)) {
      dispatchFetchInfluencersRecentPosts([id], channel, {} as DiscoveryCreatorsFilters, page, true);
    }
  };

  handleNoImportTaskBannerShown = (flag: boolean): void => {
    this.setState({ noImportTaskBannersShown: flag });
  };

  renderEmptyMessage(): React.ReactNode {
    const { canManageList } = this.props;

    return (
      <div className="lists-results__empty">
        <div className="lists-results__empty-contents">
          {t('lists.results_no_influencers_selected')}
        </div>
        {
          canManageList && (
            <div className="lists-results__empty-contents">
              <div>
                <ActionButton link="/content-creator/profiles">
                  {t('lists.button_add_creator_from_discovery')}
                </ActionButton>
              </div>
              <div className="lists-results__empty-content-wrapper">
                <CreateNewProfilesButton openedOn="custom-collection" />
              </div>
            </div>
          )
        }
      </div>
    );
  }

  renderImportTasksBanners(): React.ReactNode | null {
    const { currentList: { lightweightInfluencersImportTasks, type } } = this.props;

    return type !== SMART_LIST_TYPE ? (
      <ListImportTaskStatusBanners
        importTasks={lightweightInfluencersImportTasks ?? []}
        onAllBannersHidden={this.handleNoImportTaskBannerShown}
      />
    ) : null;
  }

  render(): React.ReactNode {
    const {
      influencers,
      influencerRecentPosts,
      expandedIdsMap,
      contextMenuProps,
      updateCreatorTileSize,
      onDelete
    } = this.props;
    const { groupedCreatorIds, noImportTaskBannersShown } = this.state;

    return (
      <section className="lists-results">
        {this.renderImportTasksBanners()}
        {!isEmpty(influencers) ? (
          <ListsInfluencerCards
            influencers={influencers}
            influencerRecentPosts={influencerRecentPosts}
            groupedCreatorIds={groupedCreatorIds}
            expandedIdsMap={expandedIdsMap}
            contextMenuProps={contextMenuProps}
            updateCreatorTileSize={updateCreatorTileSize}
            onDelete={onDelete}
            onFetchInfluencerPosts={this.handleFetchCreatorRecentPosts}
          />
        ) : null}
        {isEmpty(influencers) && noImportTaskBannersShown ? (
          this.renderEmptyMessage()
        ) : null}
      </section>
    );
  }
}

const mapStateToProps = ({ influencerRecentPosts, lists }): StateProps => ({
  currentList: lists.currentList.list,
  influencerRecentPosts: influencerRecentPosts.recentPostsData
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchInfluencersRecentPosts: (creatorsList: Array<number>, channelType: string, filters: DiscoveryCreatorsFilters, page: number, isCancelable: boolean) => (
    dispatch(fetchInfluencersRecentPosts(creatorsList, channelType, filters, page, isCancelable, true))
  ),

  dispatchFetchInfluencersMeta: (creatorsList: Array<number>) => (
    dispatch(fetchInfluencersMeta(creatorsList))
  ),

  dispatchFetchInfluencersHistory: (orgId: number, creatorsList: Array<number>) => (
    dispatch(fetchInfluencersHistory(orgId, creatorsList))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(ListsResults);
